/**
 * Admin "view as student" state for the student app.
 *
 * Mirrors the teacher app's `teacher-impersonation-id` localStorage pattern,
 * but the identity travels as a request header (`x-impersonate-user-id`)
 * read by the school backend's context, so every query — school, graph and
 * community — answers as the chosen student without per-procedure plumbing.
 * The backend only honours it for `admin:all` callers and blocks mutations.
 */
export const STORAGE_KEY = 'p2e-impersonation-student';
export const IMPERSONATE_USER_HEADER = 'x-impersonate-user-id';

export type ImpersonatedStudent = {
  userId: string;
  firstName: string;
  lastName: string;
  nickname?: string | null;
  email?: string | null;
  profileImageUrl?: string | null;
};

const getStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

export const readImpersonatedStudent = (): ImpersonatedStudent | null => {
  const storage = getStorage();
  if (!storage) return null;
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ImpersonatedStudent> | null;
    if (!parsed || typeof parsed.userId !== 'string' || !parsed.userId) return null;
    return {
      userId: parsed.userId,
      firstName: parsed.firstName ?? '',
      lastName: parsed.lastName ?? '',
      nickname: parsed.nickname ?? null,
      email: parsed.email ?? null,
      profileImageUrl: parsed.profileImageUrl ?? null,
    };
  } catch {
    storage.removeItem(STORAGE_KEY);
    return null;
  }
};

export const writeImpersonatedStudent = (student: ImpersonatedStudent): void => {
  getStorage()?.setItem(STORAGE_KEY, JSON.stringify(student));
};

export const clearImpersonatedStudent = (): void => {
  getStorage()?.removeItem(STORAGE_KEY);
};

/**
 * True while an admin is viewing as a student. Blocking pop-ups (feedback
 * surveys, announcements) stay closed then: the session is read-only, so
 * they could never be answered or acknowledged and would trap the viewer.
 * Changing the selection reloads the page, so reading it once is enough.
 */
export const isImpersonating = (): boolean => readImpersonatedStudent() !== null;

/** Headers for the tRPC clients: `{}` when not viewing as a student. */
export const impersonationHeaders = (): Record<string, string> => {
  const student = readImpersonatedStudent();
  return student ? { [IMPERSONATE_USER_HEADER]: student.userId } : {};
};

export const studentDisplayName = (student: Pick<ImpersonatedStudent, 'firstName' | 'lastName' | 'nickname' | 'email'>): string =>
  `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim() || student.nickname || student.email || '';
